import { Injectable } from '@angular/core';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { environment } from 'src/environments/environment';

/**
 * ═══ OPENING A CUSTOMER'S PROJECT FROM THE CEO PAGES ════════════════════════
 *
 * "Open" used to send the CEO to `web.totalum.app/table/totalum_instances?organizationid=…`
 * — the tables view of the ADMIN panel filtered to that project, which is not the
 * project. This opens the project itself, in the same session a customer would get,
 * using the same deep link the `/account` page's Open button has always produced.
 *
 * ── ⚠️ WHY THE URL IS FETCHED AND NOT BUILT HERE ────────────────────────────
 *
 * The `/account` page can build it in the browser because it holds
 * `instance.defaultUser` — an encrypted credential — for its OWN projects. The CEO
 * lists are every project of every customer, and they deliberately strip that field.
 * Putting it back would mean one list request returning live credentials for the whole
 * fleet, cached in the browser and sitting in devtools.
 *
 * So the link is minted per project, on the click, by
 * `POST /ceo-dashboard/projects/:organizationId/open-url`, behind a password.
 *
 * ── ⚠️ WHAT THE STORED PASSWORD IS AND IS NOT ───────────────────────────────
 *
 * `localStorage` holds it after the first correct entry so nobody retypes it, and it is
 * replayed on every call. That is a CONVENIENCE, not a gate: the server re-checks it
 * every time, in constant time, with a lockout. Nothing here decides whether the CEO
 * is allowed to open a project — it decides whether to show them a prompt first.
 *
 * ⚠️ A REJECTED STORED PASSWORD IS DELETED IMMEDIATELY. Otherwise a rotated password
 * would leave every click failing silently against a stale value, with no way to get
 * the prompt back short of clearing site data.
 */

const STORAGE_KEY = 'ceo_open_project_password';

export interface OpenProjectOutcome {
    /** The tab was opened. */
    opened: boolean;
    /** True when the caller should ask for the password and try again. */
    needsPassword: boolean;
    /** Ready to show. `null` when there is nothing to say. */
    error: string | null;
}

@Injectable({ providedIn: 'root' })
export class CeoOpenProjectService {

    constructor(private api: ApiSdkService) {}

    /**
     * `{base}auth/login?auth={encoded}&organizationId={id}` — the exact shape
     * `account.page.ts`, `onboarding-flow.page.ts` and `sign-in-flow.page.ts` all
     * build. `null` when the response carried no credential.
     */
    private buildUrl(data: { auth?: string; organizationId?: string } | undefined): string | null {
        const auth = data?.auth;
        const organizationId = data?.organizationId;
        const configured = environment.totalumFrontendUrl;
        if (!auth || !organizationId || !configured) return null;

        // `environment.ts` has no trailing slash while the other two do; normalise.
        const base = configured.endsWith('/') ? configured : `${configured}/`;
        return `${base}auth/login?auth=${encodeURIComponent(auth)}&organizationId=${encodeURIComponent(organizationId)}`;
    }

    /** The remembered password, or `''`. */
    get storedPassword(): string {
        try {
            return localStorage.getItem(STORAGE_KEY) || '';
        } catch {
            // Private mode or storage disabled: the prompt simply appears every time.
            return '';
        }
    }

    get isUnlocked(): boolean {
        return !!this.storedPassword;
    }

    private remember(password: string): void {
        try {
            localStorage.setItem(STORAGE_KEY, password);
        } catch {
            /* nothing to do — see `storedPassword` */
        }
    }

    forget(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* ignore */
        }
    }

    /**
     * Fetch the link and open it.
     *
     * ⚠️⚠️ `window.open` IS CALLED **SYNCHRONOUSLY AFTER AN `await`**, WHICH POPUP
     * BLOCKERS NORMALLY REFUSE — and it works here only because the whole call chain
     * starts from the click. Browsers allow one deferred `window.open` per user
     * gesture; a second one, or one fired from a timer, is blocked. So this must never
     * be moved behind a `setTimeout`, and the caller must not `await` anything else
     * between the click and here.
     *
     * @param password when given, tried and remembered on success. Otherwise the
     *                 stored one is used, which is the normal path once unlocked.
     */
    async open(organizationId: string, password?: string): Promise<OpenProjectOutcome> {
        if (!organizationId) {
            return { opened: false, needsPassword: false, error: 'This project has no id.' };
        }

        const supplied = (password ?? this.storedPassword) || '';

        if (!supplied) {
            // Nothing to try. Ask, without troubling the server.
            return { opened: false, needsPassword: true, error: null };
        }

        const response = await this.api.getCeoProjectOpenUrl(organizationId, supplied);
        const data = response?.success?.data;

        /**
         * ⚠️⚠️ THE URL IS BUILT HERE, FROM **THIS BUILD'S** `environment.totalumFrontendUrl`
         * — NOT from the `url` the server sent. THIS IS THE FIX for "it opens
         * web.totalum.app but the login does not work".
         *
         * The server has to derive its base from `APP_ENVIRONMENT`, which is a SECOND
         * source of truth for which Totalum app to open. `/account`, the onboarding flow
         * and the sign-in flow all use the constant below. When the backend's guess and
         * the frontend's constant disagree, the tab lands on the wrong app, that app
         * cannot decrypt a credential minted for the other one, and the login fails with
         * nothing to explain why.
         *
         * Using the same constant the rest of the product uses makes the CEO link
         * byte-identical to `/account`'s by construction.
         *
         * ⚠️ THE BASE IS COMPILED IN, NOT USER INPUT — so this is not an open redirect.
         * The server's `url` remains the fallback for the (impossible in this app) case
         * of a build with no configured frontend.
         */
        const url = this.buildUrl(data) || data?.url;

        if (url) {
            // Only remembered once the SERVER has accepted it.
            this.remember(supplied);

            const tab = window.open(url, '_blank', 'noopener,noreferrer');

            /*
              ⚠️ A BLOCKED POPUP IS REPORTED, NOT SWALLOWED. `window.open` returns
              `null` when the browser refuses — which it can, because this fires after
              an `await` rather than directly inside the click handler. Returning
              `opened: true` there would leave the operator staring at a button that
              does nothing and no reason why.

              ⚠️ THE URL IS **NOT** HANDED BACK FOR THE PAGE TO RENDER AS A LINK. It
              carries a live credential, and putting it in the DOM would leave it in the
              page for anything to read. Allowing popups once is the fix, and the
              message says so.
            */
            if (!tab) {
                return {
                    opened: false,
                    needsPassword: false,
                    error: 'Your browser blocked the new tab. Allow pop-ups for this page, then click Open again.',
                };
            }

            return { opened: true, needsPassword: false, error: null };
        }

        const errors = response?.error?.errors || response?.success?.errors || {};
        const code = errors.errorCode;
        const message = errors.errorMessage;

        if (code === 'PASSWORD_INCORRECT' || code === 'PASSWORD_REQUIRED') {
            /*
              ⚠️ THE STORED VALUE IS DROPPED THE MOMENT THE SERVER REJECTS IT. This is
              what makes a rotated password recoverable: the next click prompts again
              instead of silently replaying something that will never work.
            */
            this.forget();
            return {
                opened: false,
                needsPassword: true,
                error: code === 'PASSWORD_INCORRECT' ? (message || 'That password is not correct.') : null,
            };
        }

        if (code === 'TOO_MANY_ATTEMPTS') {
            // Do NOT re-prompt: another guess cannot succeed until the lockout expires,
            // and a prompt would invite exactly that.
            return { opened: false, needsPassword: false, error: message || 'Too many attempts. Try again later.' };
        }

        return {
            opened: false,
            needsPassword: false,
            error: message || 'Could not open this project.',
        };
    }

    /**
     * ⭐⭐ A ONE-TIME LINK INTO A CUSTOMER'S **NEW PLATFORM** ACCOUNT.
     *
     * ⚠️ THIS ONE RETURNS THE URL INSTEAD OF OPENING IT, which is the exact opposite
     * of `open()` above and is not an oversight — it is what was asked for: a link
     * the admin copies and pastes into another window. `open()` refuses to hand its
     * URL back because that URL wraps a permanent stored credential; this one can,
     * because what it carries is a ticket that account-backend mints per click,
     * stores only as a SHA-256, expires in five minutes and destroys on first use.
     * Rendering it is survivable precisely because it is worthless a moment later.
     *
     * ⚠️ THE PASSWORD HANDLING IS SHARED WITH `open()` ON PURPOSE — same endpoint
     * family, same server-side lockout counter, same "forget a rejected password"
     * rule. Two different unlock states for two buttons of equal power would be a
     * way to get one of them wrong.
     */
    async platformLoginUrl(
        userId: string,
        password?: string,
    ): Promise<{ url: string | null; expiresAt: string | null; needsPassword: boolean; error: string | null }> {
        if (!userId) {
            return { url: null, expiresAt: null, needsPassword: false, error: 'This customer has no id.' };
        }

        const supplied = (password ?? this.storedPassword) || '';
        if (!supplied) {
            return { url: null, expiresAt: null, needsPassword: true, error: null };
        }

        const response = await this.api.getCeoPlatformLoginUrl(userId, supplied);
        const data = response?.success?.data;

        if (data?.url) {
            // Only remembered once the SERVER has accepted it — same rule as `open()`.
            this.remember(supplied);
            return { url: data.url, expiresAt: data.expiresAt || null, needsPassword: false, error: null };
        }

        const errors = response?.error?.errors || response?.success?.errors || {};
        const code = errors.errorCode;
        const message = errors.errorMessage;

        if (code === 'PASSWORD_INCORRECT' || code === 'PASSWORD_REQUIRED') {
            this.forget();
            return {
                url: null,
                expiresAt: null,
                needsPassword: true,
                error: code === 'PASSWORD_INCORRECT' ? (message || 'That password is not correct.') : null,
            };
        }

        if (code === 'TOO_MANY_ATTEMPTS') {
            return { url: null, expiresAt: null, needsPassword: false, error: message || 'Too many attempts. Try again later.' };
        }

        return {
            url: null,
            expiresAt: null,
            needsPassword: false,
            error: message || 'Could not create a login link for this customer.',
        };
    }
}
